import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { FilterMatchMode } from 'primevue/api'
import type { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable'

// Store
import useCompany from '@/store/company'
import useLoading from '@/store/load'

// Services
import { getPayrollsConfigurations } from '../services/payrollsConfigService'

// Types
import type { PayrollsConfig } from '../models'
import { useUserStore } from '@/store'

export function usePayrollsConfig() {
  const companyStore = useCompany()
  const { company } = storeToRefs(companyStore)
  const { user } = storeToRefs(useUserStore())
  const loadingStore = useLoading()
  const { isChargingBatteries } = storeToRefs(loadingStore)

  const payrollsConfig = ref<PayrollsConfig[]>([])
  const totalRecords = ref(0)

  const lazyParams = ref({
    offset: 0,
    limit: 10,
    sortField: 'updatedAt',
    sortOrder: -1,
    filters: {
      global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    },
  })

  const paginationParams = computed(() => ({
    companyId: company.value?.id,
    offset: lazyParams.value.offset,
    limit: lazyParams.value.limit,
    search: lazyParams.value.filters.global.value,
    sortField: lazyParams.value.sortField,
    sortOrder: (lazyParams.value.sortOrder === 1 ? 'asc' : 'desc') as
      | 'asc'
      | 'desc',
  }))

  const fetchData = async () => {
    loadingStore.setLoadingState()
    try {
      const { data, meta } = await getPayrollsConfigurations({
        clientId: user.value?.clientId,
        companyId: paginationParams.value.companyId,
        pagination: {
          limit: paginationParams.value.limit,
          offset: paginationParams.value.offset,
          search: paginationParams.value.search,
          sortField: paginationParams.value.sortField,
          sortOrder: paginationParams.value.sortOrder,
        },
      })

      payrollsConfig.value = data
      totalRecords.value = meta.total
    } catch (error) {
      console.error('Failed to fetch payroll configurations:', error)
      payrollsConfig.value = []
      totalRecords.value = 0
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  watch(
    paginationParams,
    () => {
      fetchData()
    },
    { deep: true }
  )

  watch(
    company,
    () => {
      lazyParams.value.offset = 0
      fetchData()
    },
    { immediate: true }
  )

  const onPage = (event: DataTablePageEvent) => {
    lazyParams.value.offset = event.page + 1
    lazyParams.value.limit = event.rows
  }

  const onSort = (event: DataTableSortEvent) => {
    lazyParams.value.sortField = event.sortField as string
    lazyParams.value.sortOrder = event.sortOrder as number
  }

  const onFilter = (searchValue: string) => {
    lazyParams.value.filters.global.value = searchValue
    lazyParams.value.offset = 0
  }

  const currentPageReportTemplate = computed(
    () => `{first} a {last} de ${totalRecords.value}`
  )

  return {
    payrollsConfig,
    totalRecords,
    lazyParams,
    isChargingBatteries,
    currentPageReportTemplate,
    onPage,
    onSort,
    onFilter,
  }
}
